import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { EmptyBoxIcon } from '@/components/icons';
import { getClientErrorLevel, getServerErrorLevel } from '@/config/httpStats';
import { formatDateTime, formatRelativeTime } from '@/utils/date';
import { formatCompactNumber, formatNumber } from '@/utils/format';
import type { DomainHttpStats } from '@/types/stats';
import { RateBadge } from './RateBadge';
import { StatusChip } from './StatusChip';
import { StatusClassBar } from './StatusClassBar';
import './stats.css';

export interface SitesStatsTableProps {
  /** Shown in the order received (the server sorts by 5xx, then requests) */
  domains: DomainHttpStats[];
  isLoading: boolean;
  /** Link that opens the drill-down of a domain */
  getDomainHref: (domain: string) => string;
}

const COLUMN_COUNT = 8;

/**
 * One row per site: request volume, status mix, 5xx/4xx counts and rates
 * (highlighted past the thresholds in config/httpStats.ts) and the most
 * frequent statuses
 */
export function SitesStatsTable({ domains, isLoading, getDomainHref }: SitesStatsTableProps) {
  return (
    <Table isLoading={isLoading} skeletonColumns={COLUMN_COUNT} className="sites-stats-table">
      <TableHead>
        <TableRow>
          <TableHeader>Domain</TableHeader>
          <TableHeader align="right">Requests</TableHeader>
          <TableHeader>Status mix</TableHeader>
          <TableHeader align="right">5xx</TableHeader>
          <TableHeader align="right">4xx</TableHeader>
          <TableHeader>Top statuses</TableHeader>
          <TableHeader>Last request</TableHeader>
          <TableHeader>Nodes</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {domains.length === 0 ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <TableEmpty
                icon={<EmptyBoxIcon />}
                message="No requests in this period. Statistics appear a few minutes after nodes start reporting."
              />
            </TableCell>
          </TableRow>
        ) : (
          domains.map((site) => (
            <TableRow key={site.domain} data-testid="sites-stats-row">
              <TableCell>
                <Link to={getDomainHref(site.domain)} className="sites-stats-domain">
                  {site.domain}
                </Link>
              </TableCell>
              <TableCell align="right" className="sites-stats-number" title={formatNumber(site.requests)}>
                {formatCompactNumber(site.requests)}
              </TableCell>
              <TableCell className="sites-stats-mix">
                <StatusClassBar counts={site.status_classes} label={`${site.domain} status mix`} />
              </TableCell>
              <TableCell align="right">
                <span className="sites-stats-errors">
                  <span className="sites-stats-number">{formatNumber(site.status_classes['5xx'])}</span>
                  <RateBadge rate={site.error_rate} level={getServerErrorLevel(site.error_rate)} label="5xx rate" />
                </span>
              </TableCell>
              <TableCell align="right">
                <span className="sites-stats-errors">
                  <span className="sites-stats-number">{formatNumber(site.status_classes['4xx'])}</span>
                  <RateBadge
                    rate={site.client_error_rate}
                    level={getClientErrorLevel(site.client_error_rate)}
                    label="4xx rate"
                  />
                </span>
              </TableCell>
              <TableCell>
                <span className="sites-stats-chips">
                  {site.top_statuses.map((item) => (
                    <StatusChip key={item.status} status={item.status} count={item.count} />
                  ))}
                </span>
              </TableCell>
              <TableCell>
                <span className="sites-stats-muted" title={formatDateTime(site.last_request_at)}>
                  {formatRelativeTime(site.last_request_at)}
                </span>
              </TableCell>
              <TableCell>
                <span
                  className="sites-stats-nodes"
                  title={site.nodes.map((node) => `${node.node_name}: ${formatNumber(node.requests)} requests`).join('\n')}
                >
                  {site.nodes.map((node) => node.node_name).join(', ') || '-'}
                </span>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
